import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Clock, ChevronDown, Lock, Star, ArrowUpDown, HelpCircle, Check } from 'lucide-react';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import { usePlan } from '../App';
import { NEWSLETTERS } from './MesNewsletters';
import MultiDropdownSelect from '../components/MultiDropdownSelect';
import {
  cleanLocation, extractCity, getContrat,
  getOfferBadge, formatRelative,
  buildVilleOptions, buildContratOptions,
  SourceInfo,
} from '../utils/offerUtils';

const API = 'https://aglomera.com/api';
const PUB = process.env.PUBLIC_URL || '';

export const SOURCE_LOGO: Record<string, SourceInfo> = {
  wttj:              { logo: `${PUB}/portails/logo-wttj.png`,           label: 'WTTJ' },
  apec_stage:        { logo: `${PUB}/portails/logo-apec.png`,           label: 'APEC' },
  apec_emploi:       { logo: `${PUB}/portails/logo-apec.png`,           label: 'APEC' },
  apec:              { logo: `${PUB}/portails/logo-apec.png`,           label: 'APEC' },
  hellowork:         { logo: `${PUB}/portails/logo-hellowork.png`,      label: 'HelloWork' },
  jobteaser:         { logo: `${PUB}/portails/logo-jobteaser.png`,      label: 'JobTeaser' },
  jobijoba:          { logo: `${PUB}/portails/logo-jobijoba.png`,       label: 'Jobijoba' },
  meteojob:          { logo: `${PUB}/portails/logo-meteojob.png`,       label: 'Météojob' },
  figaro:            { logo: `${PUB}/portails/logo-figaro.png`,         label: 'Figaro Emploi' },
  cadremploi:        { logo: `${PUB}/portails/logo-figaro.png`,         label: 'Cadremploi' },
  jooble:            { logo: `${PUB}/portails/logo-jooble.png`,         label: 'Jooble' },
  linkedin:          { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  'linkedin-jobs':   { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  'linkedin-posts':  { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  linkedin_job:      { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  linkedin_jobs:     { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  linkedin_post:     { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  linkedin_posts:    { logo: `${PUB}/portails/logo-linkedin.png`,       label: 'LinkedIn' },
  adzuna:            { logo: `${PUB}/portails/logo-adzuna.png`,         label: 'Adzuna' },
  france_travail:    { logo: `${PUB}/portails/logo-france-travail.png`, label: 'France Travail' },
  indeed:            { logo: `${PUB}/portails/logo-indeed.png`,         label: 'Indeed' },
  glassdoor:         { logo: `${PUB}/portails/logo-glassdoor.png`,      label: 'Glassdoor' },
  michaelpage:       { logo: `${PUB}/portails/logo-michaelpage.png`,    label: 'Michael Page' },
  'michael-page':    { logo: `${PUB}/portails/logo-michaelpage.png`,    label: 'Michael Page' },
  wizbii:            { logo: `${PUB}/portails/logo-wizbii.png`,         label: 'Wizbii' },
};

// Ré-exporte pour backward compat (NewsletterDetail l'importe)
export { getContrat };

interface Offre {
  id: number;
  title: string;
  company: string;
  location: string;
  url: string;
  date: string;
  source: string;
  remuneration: string;
  type_contrat_detail: string;
  is_new: boolean;
}

interface AbonnementDetail {
  id: number;
  newsletter_slug: string;
  localisation: string;
  types_contrat: string[];
}


// ── Ligne d'offre ─────────────────────────────────────────────────────────────
interface OfferRowProps {
  offre: Offre;
  couleur: string;
  isFavori: boolean;
  onToggleFavori: (offre: Offre) => void;
  toggling: boolean;
  justSaved: boolean;
}

const OfferRow: React.FC<OfferRowProps> = ({ offre, couleur, isFavori, onToggleFavori, toggling, justSaved }) => {
  const badge = getOfferBadge(offre, SOURCE_LOGO);
  const contratDetecte = getContrat(offre);
  const locClean = cleanLocation(offre.location);
  const sourceLabel = badge.label;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-4 hover:border-gray-200 hover:shadow-sm transition-all">
      {/* Badge source : logo ou initiales */}
      <div className="w-10 h-10 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center flex-shrink-0 p-1.5">
        {badge.type === 'logo' ? (
          <img
            src={badge.logo}
            alt={badge.label}
            className="w-full h-full object-contain"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                // L5 : textContent à la place de innerHTML (pas d'injection XSS)
                const init = badge.label.replace(/[_-]/g,' ').split(/\s+/).filter(Boolean)
                  .slice(0,3).map((w:string)=>w[0]).join('').toUpperCase() || '??';
                const span = document.createElement('span');
                span.style.cssText = 'font-size:9px;font-weight:700;color:#6b7280;letter-spacing:0.03em';
                span.textContent = init;
                parent.appendChild(span);
              }
            }}
          />
        ) : (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em' }}>
            {badge.text}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate max-w-[480px]">{offre.title}</h3>
          {offre.is_new && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: '#dcfce7', color: '#16a34a' }}>Nouveau</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-0.5 truncate">
          {offre.company || '—'}
          {locClean ? ` · ${locClean}` : ''}
          {sourceLabel && sourceLabel !== 'Inconnu' ? ` · via ${sourceLabel}` : ''}
        </p>
        {contratDetecte !== 'Non spécifié' && (
          <span className="inline-block text-[10px] mt-1 px-2 py-0.5 rounded-full font-semibold"
            style={{ background: `${couleur}15`, color: couleur }}>
            {contratDetecte}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {offre.date && (
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            <Clock size={11} />
            {formatRelative(offre.date)}
          </div>
        )}

        {/* Bouton étoile favori */}
        <button
          onClick={() => onToggleFavori(offre)}
          disabled={toggling}
          title={isFavori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          className="p-1.5 rounded-xl transition-all"
          style={{
            color: isFavori ? '#f59e0b' : '#d1d5db',
            background: justSaved ? '#fef3c7' : 'transparent',
            transform: justSaved ? 'scale(1.3)' : 'scale(1)',
            transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <Star
            size={16}
            fill={isFavori ? '#f59e0b' : 'none'}
            stroke={isFavori ? '#f59e0b' : '#d1d5db'}
          />
        </button>

        {offre.url && (
          <a href={offre.url} target="_blank" rel="noopener noreferrer"
            className="p-1.5 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-all">
            <ExternalLink size={15} />
          </a>
        )}
      </div>
    </div>
  );
};

// ── Feed d'une newsletter ─────────────────────────────────────────────────────
const NewsletterFeed: React.FC<{ abonnement: AbonnementDetail }> = ({ abonnement }) => {
  const navigate = useNavigate();
  const { plan } = usePlan();
  const newsletter = NEWSLETTERS.find(n => n.slug === abonnement.newsletter_slug);
  const [offres, setOffres] = useState<Offre[]>([]);
  const [loadingOffres, setLoadingOffres] = useState(true);
  const [villesFiltres, setVillesFiltres] = useState<string[]>([]);
  const [contratsFiltres, setContratsFiltres] = useState<string[]>([]);
  const [showFiltres, setShowFiltres] = useState(false);

  // Tri : 'pertinence' (défaut, ordre backend) ou 'date' (publication, récente→ancienne)
  const [showSort, setShowSort] = useState(false);
  const [sortMode, setSortMode] = useState<'pertinence' | 'date'>(
    () => (localStorage.getItem('alertes_sort_mode') as 'pertinence' | 'date') || 'pertinence'
  );
  useEffect(() => { localStorage.setItem('alertes_sort_mode', sortMode); }, [sortMode]);

  // Favoris
  const [favoriUrls, setFavoriUrls] = useState<Set<string>>(new Set());
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [justSavedId, setJustSavedId] = useState<number | null>(null);

  // Charger les favoris existants
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      fetch(`${API}/favoris/`, { headers: await apiHeaders() })
        .then(r => r.ok ? r.json() : [])
        .then((data: { url: string }[]) => {
          setFavoriUrls(new Set(data.map(f => f.url)));
        });
    })();
  }, []);

  const loadOffres = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoadingOffres(true);
    try {
      const res = await fetch(`${API}/newsletters/${abonnement.id}/offres/`, {
        headers: await apiHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setOffres(data.offres || []);
      }
    } finally {
      setLoadingOffres(false);
    }
  }, [abonnement.id]);

  useEffect(() => { loadOffres(); }, [loadOffres]);

  const handleToggleFavori = async (offre: Offre) => {
    const user = auth.currentUser;
    if (!user) return;
    setTogglingId(offre.id);
    try {
      const res = await fetch(`${API}/favoris/toggle/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ offre_id: offre.id }),
      });
      if (res.ok) {
        const data = await res.json();
        setFavoriUrls(prev => {
          const next = new Set(prev);
          if (data.favori) {
            next.add(offre.url);
            setJustSavedId(offre.id);
            setTimeout(() => setJustSavedId(null), 800);
          } else {
            next.delete(offre.url);
          }
          return next;
        });
      }
    } finally { setTogglingId(null); }
  };

  if (!newsletter) return null;

  const isPro = plan === 'pro' || plan === 'premium';

  const offresFiltered = offres.filter(o => {
    if (villesFiltres.length > 0 && !villesFiltres.includes(extractCity(o.location))) return false;
    if (contratsFiltres.length > 0 && !contratsFiltres.includes(getContrat(o))) return false;
    return true;
  });

  // Le backend gère la sélection gratuite (freezée) — on affiche tout ce qui est renvoyé
  const offresVisibles = offresFiltered;
  const villeOptions = buildVilleOptions(offres);
  const contratOptions = buildContratOptions(offres);

  // Tri appliqué SÉPARÉMENT dans chaque groupe (nouvelles / anciennes).
  // pertinence = ordre backend conservé ; date = publication récente→ancienne (non parsable en fin).
  const sortGroup = (arr: Offre[]) => {
    if (sortMode !== 'date') return arr;
    return [...arr].sort((a, b) => {
      const ta = new Date(a.date).getTime();
      const tb = new Date(b.date).getTime();
      return (isNaN(tb) ? -Infinity : tb) - (isNaN(ta) ? -Infinity : ta);
    });
  };
  const newOffres = sortGroup(offresVisibles.filter(o => o.is_new));
  const oldOffres = sortGroup(offresVisibles.filter(o => !o.is_new));

  const renderRow = (offre: Offre) => (
    <OfferRow
      key={offre.id}
      offre={offre}
      couleur={newsletter.couleur}
      isFavori={favoriUrls.has(offre.url)}
      onToggleFavori={handleToggleFavori}
      toggling={togglingId === offre.id}
      justSaved={justSavedId === offre.id}
    />
  );

  const SORT_HELP =
    "Par défaut, les offres sont classées par pertinence selon votre profil " +
    "(postes visés, puis localisation et type de contrat). Vous pouvez aussi trier " +
    "par date de publication (de la plus récente à la plus ancienne). Le tri s'applique " +
    "séparément aux nouvelles offres et aux offres précédentes.";

  return (
    <div className="w-full">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ minHeight: 160 }}>
        <img src={newsletter.image} alt={newsletter.nom}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `${newsletter.couleur}d0` }} />
        <div className="relative z-10 px-8 py-8">
          <h1 className="text-2xl font-bold text-white drop-shadow">{newsletter.nom}</h1>
          <p className="text-sm text-white opacity-90 mt-1 drop-shadow">{newsletter.tagline}</p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {newsletter.keywords.map(kw => (
              <span key={kw} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white bg-opacity-20 text-white">
                {kw}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="px-8 py-6">
        {/* Barre d'actions */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {!loadingOffres && (
              <span className="text-sm font-semibold text-gray-700">
                {offres.length} offre{offres.length > 1 ? 's' : ''}
              </span>
            )}
            {!isPro && (
              <span className="text-xs text-gray-400 font-medium px-2 py-0.5 rounded-full" style={{ background: '#f3f4f6' }}>
                Sélection gratuite · Plan Pro pour tout voir
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Tri */}
            <div className="relative">
              <button onClick={() => { setShowSort(s => !s); setShowFiltres(false); }}
                className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border-2 transition-all"
                style={{ color: sortMode === 'date' ? newsletter.couleur : '#6b7280',
                         borderColor: sortMode === 'date' ? newsletter.couleur : '#e5e7eb' }}>
                <ArrowUpDown size={14} />
                Tri
                <ChevronDown size={13} className={showSort ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
              {showSort && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl border-2 shadow-lg z-20 p-1.5"
                  style={{ borderColor: `${newsletter.couleur}30` }}>
                  {([
                    { key: 'pertinence', label: 'Pertinence', sub: 'Selon votre profil (défaut)' },
                    { key: 'date',       label: 'Date de publication', sub: 'Plus récente → plus ancienne' },
                  ] as const).map(opt => (
                    <button key={opt.key}
                      onClick={() => { setSortMode(opt.key); setShowSort(false); }}
                      className="w-full flex items-start gap-2 px-3 py-2 rounded-xl text-left transition-all hover:bg-gray-50">
                      <span className="w-4 flex-shrink-0 pt-0.5">
                        {sortMode === opt.key && <Check size={14} style={{ color: newsletter.couleur }} />}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold"
                          style={{ color: sortMode === opt.key ? newsletter.couleur : '#374151' }}>{opt.label}</span>
                        <span className="block text-[11px] text-gray-400">{opt.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Aide tri */}
            <div className="relative group flex items-center">
              <HelpCircle size={15} className="text-gray-300 hover:text-gray-400 cursor-help transition-colors" />
              <div className="hidden group-hover:block absolute right-0 top-7 w-72 bg-gray-900 text-white text-[11px] leading-relaxed rounded-xl p-3 z-30 shadow-xl">
                {SORT_HELP}
              </div>
            </div>

            {/* Filtres */}
            <button onClick={() => { setShowFiltres(f => !f); setShowSort(false); }}
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border-2 transition-all"
              style={{ color: (villesFiltres.length || contratsFiltres.length) ? newsletter.couleur : '#6b7280',
                       borderColor: (villesFiltres.length || contratsFiltres.length) ? newsletter.couleur : '#e5e7eb' }}>
              Filtres
              {(villesFiltres.length + contratsFiltres.length) > 0 && (
                <span className="w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                  style={{ background: newsletter.couleur }}>
                  {villesFiltres.length + contratsFiltres.length}
                </span>
              )}
              <ChevronDown size={13} className={showFiltres ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>
          </div>
        </div>

        {/* Filtres */}
        {showFiltres && (
          <div className="mb-5 p-4 rounded-2xl border-2 bg-white"
            style={{ borderColor: `${newsletter.couleur}30` }}>
            <div className="flex flex-wrap gap-4">
              <MultiDropdownSelect
                label="Localisation"
                values={villesFiltres}
                onChange={setVillesFiltres}
                options={villeOptions}
                placeholder="Toutes les villes"
                accentColor={newsletter.couleur}
              />
              <MultiDropdownSelect
                label="Type de contrat"
                values={contratsFiltres}
                onChange={setContratsFiltres}
                options={contratOptions}
                placeholder="Tous les types"
                accentColor={newsletter.couleur}
              />
            </div>
            {(villesFiltres.length > 0 || contratsFiltres.length > 0) && (
              <button onClick={() => { setVillesFiltres([]); setContratsFiltres([]); }}
                className="mt-3 text-xs font-medium underline transition-colors"
                style={{ color: newsletter.couleur }}>
                Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* Offres */}
        {loadingOffres ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 rounded-full border-2 animate-spin"
              style={{ borderColor: newsletter.couleur, borderTopColor: 'transparent' }} />
          </div>
        ) : offres.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-12 text-center text-gray-500 text-sm">
            Aucune offre pour l'instant.
          </div>
        ) : offresFiltered.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-8 text-center text-gray-500 text-sm">
            Aucune offre pour ces filtres.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {/* Groupe NOUVELLES offres — zone à fond coloré */}
            {newOffres.length > 0 && (
              <div className="rounded-2xl p-3 flex flex-col gap-2.5"
                style={{ background: `${newsletter.couleur}0d`, border: `1px solid ${newsletter.couleur}33` }}>
                <div className="flex items-center gap-2 px-1">
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: newsletter.couleur }}>
                    Nouvelles offres
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: `${newsletter.couleur}22`, color: newsletter.couleur }}>
                    {newOffres.length}
                  </span>
                </div>
                {newOffres.map(renderRow)}
              </div>
            )}

            {/* Groupe ANCIENNES offres */}
            {oldOffres.map(renderRow)}

            {!isPro && (
              <div className="mt-3 rounded-2xl border border-gray-100 px-5 py-4 text-center"
                style={{ background: '#f9fafb' }}>
                <Lock size={16} className="mx-auto mb-2 text-gray-300" />
                <p className="text-xs text-gray-500 mb-3">
                  Passez au plan Pro pour accéder à toutes les offres en temps réel
                </p>
                <button onClick={() => navigate('/abonnement')}
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all"
                  style={{ background: newsletter.couleur }}>
                  Voir les plans
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────
const MesAlertes: React.FC = () => {
  const navigate = useNavigate();
  const [abonnements, setAbonnements] = useState<AbonnementDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      fetch(`${API}/newsletters/abonnements/`, { headers: await apiHeaders() })
        .then(r => r.ok ? r.json() : [])
        .then((data: AbonnementDetail[]) => {
          const list = Array.isArray(data) ? data : [];
          setAbonnements(list);
          if (list.length > 0) setActiveSlug(list[0].newsletter_slug);
        })
        .catch(() => setAbonnements([]))
        .finally(() => setLoading(false));
    })();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 rounded-full border-2 animate-spin"
        style={{ borderColor: '#2a52a8', borderTopColor: 'transparent' }} />
    </div>
  );

  if (abonnements.length === 0) return (
    <div className="p-8 flex flex-col items-center justify-center h-96 text-center gap-4">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2" style={{ background: '#eef2fc' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2a52a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </div>
      <h2 className="text-lg font-bold text-gray-900">Aucun abonnement actif</h2>
      <p className="text-sm text-gray-500 max-w-xs">Abonnez-vous à une newsletter thématique pour recevoir les meilleures offres.</p>
      <button onClick={() => navigate('/newsletters')}
        className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-all"
        style={{ background: '#2a52a8' }}>
        Choisir une newsletter
      </button>
    </div>
  );

  if (abonnements.length === 1) return <NewsletterFeed abonnement={abonnements[0]} />;

  const activeAbonnement = abonnements.find(a => a.newsletter_slug === activeSlug) || abonnements[0];

  return (
    <div className="w-full">
      <div className="px-8 pt-6 pb-0 border-b border-gray-100 bg-white">
        <div className="flex gap-1">
          {abonnements.map(ab => {
            const nl = NEWSLETTERS.find(n => n.slug === ab.newsletter_slug);
            const isActive = ab.newsletter_slug === activeSlug;
            return (
              <button key={ab.newsletter_slug} onClick={() => setActiveSlug(ab.newsletter_slug)}
                className="px-5 py-2.5 rounded-t-xl text-sm font-semibold transition-all border-b-2"
                style={isActive
                  ? { color: nl?.couleur || '#2a52a8', borderColor: nl?.couleur || '#2a52a8', background: 'white' }
                  : { color: '#6b7280', borderColor: 'transparent', background: '#f9fafb' }}>
                {nl?.nom || ab.newsletter_slug}
              </button>
            );
          })}
        </div>
      </div>
      <NewsletterFeed abonnement={activeAbonnement} />
    </div>
  );
};

export default MesAlertes;
